import React, { useState, useEffect, useRef } from "react";
import {
  MenuItem,
  ListItemText,
  styled,
  Avatar,
  Badge,
  Box,
  Typography,
  IconButton,
  keyframes,
  Autocomplete,
  Chip,
  TextField,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PushPinIcon from "@mui/icons-material/PushPin";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import DownloadIcon from "@mui/icons-material/Download";
import '../../styles/UserList.css';
import { addUser, removeUser, addAgent, removeAgent, getFileURL } from "../../resources/api.resource.ts";
import ConfirmDialog from "./ConfirmDialog.tsx";
import AddIcon from '@mui/icons-material/Add';
import { MemberOption } from "../../models/User.ts";
import store from "../../utils/store.ts";

const pinAnimation = keyframes`
  0% { transform: rotate(0deg) scale(1); }
  30% { transform: rotate(-10deg) scale(1.2); }
  60% { transform: rotate(50deg) scale(1.3); }
  100% { transform: rotate(45deg) scale(1); }
`;

const unpinAnimation = keyframes`
  0% { transform: rotate(45deg) scale(1); }
  30% { transform: rotate(55deg) scale(1.2); }
  60% { transform: rotate(-10deg) scale(1.3); }
  100% { transform: rotate(0deg) scale(1); }
`;

interface UserActionsProps {
  pinned: boolean;
  onAction: (action: string) => void;
  groupId: string;
  collaborationId: string;
  drawerType: string;
  list?: any[];
  listLoading?: boolean;
  refreshSidebar: () => void | undefined;
  showAddRemove: boolean;
  refreshRightPanel: () => any[];
  uId: string;
}


const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px transparent`,
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    minWidth: '10px',
  },
}));

const UserList: React.FC<UserActionsProps> = ({ pinned, onAction, groupId, collaborationId, drawerType, list, listLoading, refreshSidebar, showAddRemove, refreshRightPanel, uId }) => {
  const [drawerList, setDrawerList] = useState<any[]>(list ?? []);
  const [animating, setAnimating] = useState(false);
  const title = drawerType === 'users' ?  'Members' :  'Attachments';
  const [confirmation, setConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState<MemberOption[]>([]);
  const [members, setMembers] = useState<(MemberOption)[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getFileIcon = (fileType?: string) => {
    if (!fileType) return <InsertDriveFileIcon />;

    const type = fileType.toLowerCase();
    if (type.startsWith('image/')) return <ImageIcon />;
    if (type === 'application/pdf') return <PictureAsPdfIcon />;
    if (type.startsWith('video/')) return <VideoFileIcon />;
    if (type.startsWith('audio/')) return <AudioFileIcon />;
    if (type.includes('document') || type.includes('text')) return <DescriptionIcon />;

    return <InsertDriveFileIcon />;
  };

  const handleDownload = async (file: any) => {
    if (file.url) {
      setDownloadingId(file.id);
      try {
        const objFile = {
          collaboration_id: collaborationId,
          collaboration_instance_id: groupId,
          file_name: file.url.split("/").pop()
        };
        const resp = await getFileURL(objFile);
        if (resp && resp.url) {
          window.open(resp.url, '_blank');
        }
      } catch (error) {
        console.error('Failed to download file:', error);
        window.open(file.url, '_blank');
      } finally {
        setDownloadingId(null);
      }
    }
  };

  useEffect(() => {
    setDrawerList(list ?? []);
    setOptions(filterOptions(list ?? []));
  }, [list]);

  const handlePinClick = () => {
    setAnimating(true);
    setTimeout(() => {
      setAnimating(false);
      onAction('pin');
    }, 500);
  };

  const filterOptions = (list: any[]): MemberOption[]  => {
    let options = store.getState().allUsers;
    list.forEach(item => {
      options = options.filter((opt: MemberOption) => opt.uid !== item.id);
    });

    return options;
  }

  const openRemove = async (person: any) => {
    setSelected(person.id);
    setConfirmationText( `Are you sure you want to remove ${person.name}?`)
    setConfirmation(true);
  };

  const confirmRemove = async ()=> {
    if (!selected) return;

    setIsLoading(true);
    try {
      // Get the member being removed to check userType
      const allUsers = store.getState().allUsers;
      const memberData = allUsers.find((u: any) => u.uid === selected);
      const isAgent = memberData?.userType === 'agent';

      let resp;
      if (isAgent) {
        // Use agent-specific endpoint
        resp = await removeAgent(collaborationId, selected);
      } else {
        // Use user endpoint
        let body = {
          collaboration_id: collaborationId,
          user_uid: selected
        }
        resp = await removeUser(body);
      }

      if(resp) {
        setConfirmation(false);
        setDrawerList(drawerList.filter(item => item.id !== selected));
        refreshSidebar();
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleAdd = async () => {
    if(members.length > 0) {
      setIsLoading(true);
      try {
        // Separate users and agents
        const usersToAdd: MemberOption[] = [];
        const agentsToAdd: MemberOption[] = [];

        members.forEach((member: MemberOption) => {
          member.id = member.uid;
          if (member.userType === 'agent') {
            agentsToAdd.push(member);
          } else {
            usersToAdd.push(member);
          }
        });

        // Add users (if any) using the bulk endpoint
        if (usersToAdd.length > 0) {
          const userUids = usersToAdd.map(m => m.uid);
          let body = {
            collaboration_id: collaborationId,
            memberUids: userUids
          }
          await addUser(body);
        }

        // Add agents one by one (agent endpoint doesn't support bulk)
        if (agentsToAdd.length > 0) {
          await Promise.all(
            agentsToAdd.map(agent => addAgent(collaborationId, agent.uid))
          );
        }

        // Update UI if all succeeded
        setDrawerList(prevList => [...prevList, ...members]);
        setMembers([]);
        refreshSidebar();
      } catch (error) {
        console.error('Failed to add members:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  return (
    <>
    <Box
          role="presentation"
          className={(pinned ? "user-list-pinned" : "user-list")}
        >
        {!pinned && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#fafafa',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <Tooltip title={pinned ? "Unpin panel" : "Pin panel"}>
              <IconButton
                size="small"
                onClick={handlePinClick}
                sx={{
                  color: pinned ? '#0857c3' : 'inherit',
                  animation: animating ? (pinned ? `${unpinAnimation} 0.5s ease` : `${pinAnimation} 0.5s ease`) : 'none',
                  transform: pinned ? 'rotate(45deg)' : 'rotate(0deg)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: pinned ? 'rotate(45deg) scale(1.1)' : 'rotate(0deg) scale(1.1)',
                    backgroundColor: 'rgba(8, 87, 195, 0.08)',
                  }
                }}
              >
                <PushPinIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close panel">
              <IconButton
                size="small"
                onClick={() => onAction('close')}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        )}
         {pinned && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px 8px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: '#fafafa',
          }}
        >
          <Tooltip title="Unpin panel" placement="right">
            <IconButton
              size="small"
              onClick={handlePinClick}
              sx={{
                color: '#0857c3',
                animation: animating ? `${unpinAnimation} 0.5s ease` : 'none',
                transform: 'rotate(45deg)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'rotate(45deg) scale(1.2)',
                  backgroundColor: 'rgba(8, 87, 195, 0.08)',
                }
              }}
            >
              <PushPinIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        )}
         {!pinned && drawerType === 'users' && showAddRemove && (
          <>
          <Box
            sx={{
              padding: '12px 16px',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
          <Autocomplete
          sx={{ flex: 1 }}
          multiple
          freeSolo
          disabled={isLoading}
          options={options}
          value={members}
          onChange={(event, newValue) => {
            setMembers(newValue as MemberOption[]);
          }}
          filterOptions={(options, params) => {
            const filtered = options.filter(
              (option): option is MemberOption =>
                typeof option !== "string" &&
                option.name
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase())
            );

            if (
              params.inputValue !== "" &&
              !filtered.some(
                (opt) =>
                  opt.name.toLowerCase() === params.inputValue.toLowerCase()
              )
            ) {
              filtered.push({
                name: params.inputValue,
                email: params.inputValue,
                uid: params.inputValue,
                userType: 'user',
              });
            }
            return filtered;
          }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name
          }
          isOptionEqualToValue={(option, value) =>
            typeof option === "string" || typeof value === "string"
              ? option === value
              : option.uid === value.uid
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const fullName = typeof option === "string" ? option : option.name;
              const displayName = fullName.length > 15 ? `${fullName.substring(0, 15)}...` : fullName;
              return (
                <Chip
                  size="small"
                  label={displayName}
                  title={fullName}
                  {...getTagProps({ index })}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              size="small"
              label="Add members"
              placeholder="Type or search..."
            />
          )}
        />
          <Tooltip title="Add members">
            <Box sx={{ flexShrink: 0 }}>
              <IconButton
                onClick={() => handleAdd()}
                disabled={isLoading || members.length === 0}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.light', opacity: 0.1 },
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : <AddIcon />}
              </IconButton>
            </Box>
          </Tooltip>
          </Box>
          <Divider />
          </>
         )}
        {drawerList && drawerList.length > 0 ? (
          drawerList.map((item) => (
          <MenuItem
            key={item.id}
            sx={{
              padding: pinned ? '8px' : '10px 16px',
              transition: 'background-color 0.2s ease',
              display: 'flex',
              justifyContent: pinned ? 'center' : 'flex-start',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {drawerType === 'users' ? (
              <>
                <Tooltip title={pinned ? item.name : ""} placement="right" arrow>
                  <StyledBadge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant={item.isOnline ? "dot" : "standard"}
                    invisible={!item.isOnline}
                  >
                    <Avatar
                      sx={{
                        width: pinned ? 40 : 40,
                        height: pinned ? 40 : 40,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        fontSize: pinned ? '16px' : '16px',
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                        cursor: pinned ? 'pointer' : 'default',
                        '&:hover': pinned ? {
                          transform: 'scale(1.1)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        } : {}
                      }}
                    >
                      {item.name[0].toUpperCase()}
                    </Avatar>
                  </StyledBadge>
                </Tooltip>
                {!pinned && (
                  <ListItemText
                    primary={item.name}
                    sx={{
                      marginLeft: '12px',
                      '& .MuiListItemText-primary': {
                        fontSize: '14px',
                        fontWeight: 500,
                      }
                    }}
                  />
                )}
                {showAddRemove && !pinned && item.id !== uId && (
                  <Tooltip title="Remove member">
                    <IconButton
                      onClick={() => openRemove(item)}
                      size="small"
                      disabled={isLoading}
                      sx={{
                        marginLeft: 'auto',
                        color: 'error.main',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : (
              <>
                <Tooltip title={pinned ? item.name : ""} placement="right" arrow>
                  <Avatar
                    sx={{
                      width: pinned ? 40 : 40,
                      height: pinned ? 40 : 40,
                      backgroundColor: '#e3f2fd',
                      color: '#0857c3',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s ease',
                      cursor: pinned ? 'pointer' : 'default',
                      '&:hover': pinned ? {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        backgroundColor: 'rgba(8, 87, 195, 0.12)',
                      } : {}
                    }}
                  >
                    {getFileIcon(item.fileType)}
                  </Avatar>
                </Tooltip>
                {!pinned && (
                  <Box sx={{ marginLeft: '12px', flex: 1, overflow: 'hidden' }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name}
                    </Typography>
                    {item.uploadedBy && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '12px',
                          display: 'block',
                        }}
                      >
                        by {item.uploadedBy}
                      </Typography>
                    )}
                  </Box>
                )}
                {!pinned && (
                  <Tooltip title={downloadingId === item.id ? 'Downloading...' : 'Download'}>
                    <span>
                      <IconButton
                        onClick={() => handleDownload(item)}
                        disabled={downloadingId === item.id}
                        size="small"
                        sx={{
                          marginLeft: 'auto',
                          color: 'primary.main',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        {downloadingId === item.id
                          ? <CircularProgress size={16} color="inherit" />
                          : <DownloadIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </MenuItem>
        ))
        ) : (
          !isLoading && !listLoading && (
            <Box
              sx={{
                padding: '24px 16px',
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '14px' }}>
                {drawerType === 'users' ? 'No members yet' : 'No attachments yet'}
              </Typography>
            </Box>
          )
        )}
        {(isLoading || listLoading) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '16px',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
        <ConfirmDialog
          color="error"
          open={confirmation}
          title="Remove Member"
          message={confirmationText}
          onConfirm={confirmRemove}
          onClose={() => setConfirmation(false)}
        />
    </Box>
   
    </>
  );
};

export default UserList;
